import { canViewSmsTransactions } from "./utils/featureAccess";
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { SnackbarProvider } from "notistack";
import { Box, Typography } from "@mui/material";

import { AuthProvider, useAuth } from "./context/AuthContext";

import TopBar from "./components/Layout/TopBar";

// Public pages
import Home from "./pages/home/Home";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordFlow from "./pages/ForgotPasswordFlow";

// Transactions
import AdminTransactionsPage from "./pages/admin/TransactionPage";
import AdminCreateTransactionPage from "./pages/admin/CreateTransactionPage";
import AgentTransactionsPage from "./pages/agent/TransactionPage";
import AgentCreateTransactionPage from "./pages/agent/CreateTransactionPage";
import UpdateTransactionStatusPage from "./pages/UpdateTransactionStatusPage";
import TransactionDetailsPage from "./pages/TransactionDetailsPage";
import ReceiptPage from "./pages/ReceiptPage";

// Admin / management
import AdminTransactionDashboard from "./pages/AdminTransactionDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAgentRegistrationPage from "./pages/admin/AdminAgentRegistrationPage";
import AgentPage from "./pages/admin/AgentPage";
import AdminPage from "./pages/admin/AdminPage";
import MobileCommissionSettingsPage from "./pages/admin/MobileCommissionSettingsPage";
import AdminFeeSettingsPage from "./pages/admin/AdminFeeSettingsPage";
import FeeChangeLogPage from "./pages/FeeChangeLogPage";

// Financial pages
import CashboxesPage from "./pages/CashboxesPage";
import CashFundingsPage from "./pages/CashFundingsPage";
import ExpensesPage from "./pages/ExpensesPage";
import SalaryPaymentsPage from "./pages/SalaryPaymentsPage";
import DebtorsPage from "./pages/DebtorsPage";
import LoansPage from "./pages/LoansPage";
import FinancialDashboardPage from "./pages/FinancialDashboardPage";
import MobileDashboardPage from "./pages/MobileDashboardPage";
import MobilePayoutPage from "./pages/MobilePayoutPage";
import SmsTransactionsPage from "./pages/SmsTransactionsPage";
import MobileMoneyAccountsPage from "./pages/MobileMoneyAccountsPage";

import "./App.css";

const NotFoundPage = () => {
  return (
    <Box
      sx={{
        minHeight: "50vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: 520,
          p: 4,
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
          404
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Page introuvable
        </Typography>
        <Typography variant="body1" color="text.secondary">
          La page demandée n’existe pas ou son lien a changé.
        </Typography>
      </Box>
    </Box>
  );
};

const ProtectedRoute = ({ children, roles, access }) => {
  const { token, user } = useAuth();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const role = String(user?.role || "").toLowerCase();
  if (Array.isArray(roles) && roles.length > 0 && !roles.includes(role)) {
    const fallback = role === "agent" ? "/agent" : "/login";
    return <Navigate to={fallback} replace />;
  }

  if (access && !access(user)) return <Navigate to={role === "admin" ? "/admin/analytics" : "/agent"} replace />;

  return children;
};

const AppContent = () => {
  const { token } = useAuth();
  const location = useLocation();

  const hideTopBarRoutes = ["/", "/login", "/forgot-password"];
  const shouldShowTopBar =
    Boolean(token) && !hideTopBarRoutes.includes(location.pathname);

  return (
    <div className="App">
      {shouldShowTopBar && <TopBar />}

      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordFlow />} />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminTransactionDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminTransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/transactions/create"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminCreateTransactionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AgentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/admins"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents/register"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminAgentRegistrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fee-logs"
          element={
            <ProtectedRoute roles={["admin"]}>
              <FeeChangeLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings/fee"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminFeeSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute roles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/mobile-money-accounts"
          element={
            <ProtectedRoute roles={["admin"]}>
              <MobileMoneyAccountsPage />
            </ProtectedRoute>
          }
        />

        <Route path="/admin/settings/mobile-commission" element={<ProtectedRoute roles={["admin"]}><MobileCommissionSettingsPage /></ProtectedRoute>} />

        {/* Agent / transactions */}
        <Route
          path="/agent"
          element={
            <ProtectedRoute>
              <AgentTransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <AgentTransactionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions/create"
          element={
            <ProtectedRoute>
              <AgentCreateTransactionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions/update"
          element={
            <ProtectedRoute>
              <UpdateTransactionStatusPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions/:secret_code"
          element={
            <ProtectedRoute>
              <TransactionDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transaction/:code/receipt"
          element={
            <ProtectedRoute>
              <ReceiptPage />
            </ProtectedRoute>
          }
        />

        <Route path="/mobile-dashboard" element={<ProtectedRoute roles={["admin", "agent"]}><MobileDashboardPage /></ProtectedRoute>} />
        <Route path="/mobile-payouts/receipt" element={<ProtectedRoute roles={["admin", "agent"]}><ReceiptPage /></ProtectedRoute>} />
        <Route path="/mobile-payouts" element={<ProtectedRoute roles={["admin", "agent"]}><MobilePayoutPage /></ProtectedRoute>} />
        <Route path="/sms-transactions" element={<ProtectedRoute roles={["admin", "agent"]} access={canViewSmsTransactions}><SmsTransactionsPage /></ProtectedRoute>} />

        {/* Financial module */}
        <Route
          path="/cashboxes"
          element={
            <ProtectedRoute>
              <CashboxesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-fundings"
          element={
            <ProtectedRoute>
              <CashFundingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salary-payments"
          element={
            <ProtectedRoute>
              <SalaryPaymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/debtors"
          element={
            <ProtectedRoute>
              <DebtorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loans"
          element={
            <ProtectedRoute>
              <LoansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/financial-dashboard"
          element={
            <ProtectedRoute>
              <FinancialDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </SnackbarProvider>
    </LocalizationProvider>
  );
}

export default App;
